import React, { useState, useEffect } from 'react';
import {
    Save,
    X,
    Play,
    Clock,
    Mail,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    CheckCircle,
    Loader2,
    Code
} from 'lucide-react';
import {
    claudeQueryService,
    SavedReportDTO,
    CreateReportDTO,
    ReportExecutionDTO
} from '../../../../../services/claudeQueryService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ReportEditorProps {
    report: SavedReportDTO | null; // null = ny rapport
    initialSQL?: string;
    initialPrompt?: string;
    /** Forhåndsvalgt eksportformat (f.eks. 'chart' når rapport lages fra en chat-graf) */
    initialExportFormat?: string;
    /** Forhåndsvalgt chart-konfigurasjon (JSON) arvet fra chat-grafen */
    initialChartConfig?: string;
    onSave: () => void;
    onCancel: () => void;
}

export const ReportEditor: React.FC<ReportEditorProps> = ({
    report,
    initialSQL,
    initialPrompt,
    initialExportFormat,
    initialChartConfig,
    onSave,
    onCancel
}) => {
    const { t } = useTranslation();
    // Grunnleggende felt
    const [name, setName] = useState(report?.name || '');
    const [description, setDescription] = useState(report?.description || '');
    const [category, setCategory] = useState(report?.category || '');
    const [generatedSQL, setGeneratedSQL] = useState(report?.generatedSQL || initialSQL || '');
    const [originalPrompt, setOriginalPrompt] = useState(report?.originalPrompt || initialPrompt || '');
    const [exportFormat, setExportFormat] = useState(report?.exportFormat || initialExportFormat || 'table');
    // Chart-oppsett arvet fra chat-graf (eller eksisterende rapport). Ikke redigerbart i UI ennå —
    // bæres videre til lagring slik at widgeten arver visualiseringen.
    const chartConfig = report?.chartConfig ?? initialChartConfig;

    // Scheduling
    const [scheduleEnabled, setScheduleEnabled] = useState(report?.scheduleEnabled || false);
    const [scheduleFrequency, setScheduleFrequency] = useState('daily');
    const [scheduleHour, setScheduleHour] = useState(6);
    const [scheduleMinute, setScheduleMinute] = useState(0);
    const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(1); // Mandag
    const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);

    // Delivery
    const [deliveryMethod, setDeliveryMethod] = useState(report?.deliveryMethod || 'inapp');
    const [deliveryEmail, setDeliveryEmail] = useState(report?.deliveryEmail || '');
    const [deliveryFormat, setDeliveryFormat] = useState(report?.deliveryFormat || 'xlsx');

    // Claude
    const [useClaude, setUseClaude] = useState(report?.useClaude || false);
    const [formatPrompt, setFormatPrompt] = useState(report?.formatPrompt || '');

    // UI state
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showSchedule, setShowSchedule] = useState(scheduleEnabled);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [testResult, setTestResult] = useState<ReportExecutionDTO | null>(null);

    // Parse existing cron to UI fields
    useEffect(() => {
        if (report?.scheduleCron) {
            const parts = report.scheduleCron.split(' ');
            if (parts.length >= 5) {
                setScheduleMinute(parseInt(parts[0]) || 0);
                setScheduleHour(parseInt(parts[1]) || 6);
                if (parts[2] !== '*') {
                    setScheduleFrequency('monthly');
                    setScheduleDayOfMonth(parseInt(parts[2]) || 1);
                } else if (parts[4] !== '*') {
                    setScheduleFrequency('weekly');
                    setScheduleDayOfWeek(parseInt(parts[4]) || 1);
                } else {
                    setScheduleFrequency('daily');
                }
            }
        }
    }, [report]);

    const buildCronExpression = (): string => {
        switch (scheduleFrequency) {
            case 'daily':
                return `${scheduleMinute} ${scheduleHour} * * *`;
            case 'weekly':
                return `${scheduleMinute} ${scheduleHour} * * ${scheduleDayOfWeek}`;
            case 'monthly':
                return `${scheduleMinute} ${scheduleHour} ${scheduleDayOfMonth} * *`;
            default:
                return `${scheduleMinute} ${scheduleHour} * * *`;
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError(t('manager.modules.claudeQuery.reports.errorNameRequired', {}, 'Rapportnavn er påkrevd'));
            return;
        }
        if (!generatedSQL.trim()) {
            setError(t('manager.modules.claudeQuery.reports.errorSqlRequired', {}, 'SQL er påkrevd'));
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const dto: CreateReportDTO = {
                name: name.trim(),
                description: description.trim() || undefined,
                generatedSQL: generatedSQL.trim(),
                originalPrompt: originalPrompt.trim() || undefined,
                exportFormat,
                chartConfig: exportFormat === 'chart' ? (chartConfig || undefined) : undefined,
                category: category.trim() || undefined,
                scheduleEnabled,
                scheduleCron: scheduleEnabled ? buildCronExpression() : undefined,
                scheduleFormat: scheduleEnabled ? deliveryFormat : undefined,
                deliveryMethod: scheduleEnabled ? deliveryMethod : undefined,
                deliveryEmail: deliveryMethod === 'email' ? deliveryEmail : undefined,
                deliveryFormat: scheduleEnabled ? deliveryFormat : undefined,
                useClaude,
                formatPrompt: useClaude ? formatPrompt.trim() || undefined : undefined
            };

            if (report) {
                await claudeQueryService.updateReport(report.reportId, dto);
            } else {
                await claudeQueryService.createReport(dto);
            }

            onSave();
        } catch (err: any) {
            setError(err.message || t('manager.modules.claudeQuery.reports.saveError', {}, 'Kunne ikke lagre rapporten'));
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!report) {
            setError(t('manager.modules.claudeQuery.reports.saveFirstForTest', {}, 'Lagre rapporten først for å kjøre test'));
            return;
        }

        try {
            setTesting(true);
            setError(null);
            setTestResult(null);
            const result = await claudeQueryService.executeReport(report.reportId);
            setTestResult(result);
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.reports.testError', { '0': err.message }, `Test feilet: ${err.message}`));
        } finally {
            setTesting(false);
        }
    };

    const dayNames = [
        t('manager.modules.claudeQuery.common.sunday', {}, 'Søndag'),
        t('manager.modules.claudeQuery.common.monday', {}, 'Mandag'),
        t('manager.modules.claudeQuery.common.tuesday', {}, 'Tirsdag'),
        t('manager.modules.claudeQuery.common.wednesday', {}, 'Onsdag'),
        t('manager.modules.claudeQuery.common.thursday', {}, 'Torsdag'),
        t('manager.modules.claudeQuery.common.friday', {}, 'Fredag'),
        t('manager.modules.claudeQuery.common.saturday', {}, 'Lørdag'),
    ];

    return (
        <div className="h-full flex flex-col bg-white rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="text-lg font-bold text-gray-800">
                    {report ? t('manager.modules.claudeQuery.reports.editTitle', {}, 'Rediger rapport') : t('manager.modules.claudeQuery.reports.newReport', {}, 'Ny rapport')}
                </h2>
                <div className="flex items-center gap-2">
                    {report && (
                        <button
                            onClick={handleTest}
                            disabled={testing}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-600 rounded-md
                                     hover:bg-green-50 disabled:opacity-50 text-sm transition-colors"
                        >
                            {testing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                            {t('manager.modules.claudeQuery.reports.runTest', {}, 'Kjør test')}
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-500 text-white rounded-md
                                 hover:bg-blue-600 disabled:opacity-50 text-sm font-medium transition-colors"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {saving ? t('common.status.saving', {}, 'Lagrer...') : t('common.buttons.save', {}, 'Lagre')}
                    </button>
                    <button
                        onClick={onCancel}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-6 py-4 space-y-5">
                {/* Error */}
                {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <AlertCircle size={16} />
                        {error}
                        <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
                    </div>
                )}

                {/* Test result */}
                {testResult && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg text-sm border ${
                        testResult.status === 'completed'
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                        {testResult.status === 'completed' ? (
                            <CheckCircle size={16} />
                        ) : (
                            <AlertCircle size={16} />
                        )}
                        {testResult.status === 'completed'
                            ? t('manager.modules.claudeQuery.reports.testOk', { '0': testResult.rowCount, '1': testResult.executionTimeMs }, `Test OK: ${testResult.rowCount} rader på ${testResult.executionTimeMs}ms`)
                            : t('manager.modules.claudeQuery.reports.testError', { '0': testResult.errorMessage }, `Test feilet: ${testResult.errorMessage}`)
                        }
                    </div>
                )}

                {/* Name + Category */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.claudeQuery.reports.fieldReportName', {}, 'Rapportnavn *')}</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('manager.modules.claudeQuery.reports.reportNamePlaceholder', {}, 'f.eks. Daglig lagerstatus')}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                     focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.claudeQuery.reports.fieldCategory', {}, 'Kategori')}</label>
                        <input
                            type="text"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            placeholder={t('manager.modules.claudeQuery.reports.categoryPlaceholder', {}, 'f.eks. Lager, Ordre, KPI')}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                     focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                        />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.fields.description', {}, 'Beskrivelse')}</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t('manager.modules.claudeQuery.reports.descriptionPlaceholder', {}, 'Kort beskrivelse av hva rapporten viser...')}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none
                                 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    />
                </div>

                {/* SQL */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                        <Code size={14} />
                        {t('manager.modules.claudeQuery.reports.fieldSqlQuery', {}, 'SQL-spørring *')}
                    </label>
                    <textarea
                        value={generatedSQL}
                        onChange={(e) => setGeneratedSQL(e.target.value)}
                        placeholder="SELECT ..."
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono resize-y
                                 bg-gray-50 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    />
                </div>

                {/* Original prompt */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.claudeQuery.reports.fieldOriginalPrompt', {}, 'Opprinnelig spørsmål')}</label>
                    <input
                        type="text"
                        value={originalPrompt}
                        onChange={(e) => setOriginalPrompt(e.target.value)}
                        placeholder={t('manager.modules.claudeQuery.reports.originalPromptPlaceholder', {}, 'Spørsmålet som genererte denne SQLen...')}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    />
                </div>

                {/* Format */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.claudeQuery.reports.fieldDisplayFormat', {}, 'Visningsformat')}</label>
                    <select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white
                                 focus:outline-none focus:border-blue-400"
                    >
                        <option value="table">{t('manager.modules.claudeQuery.reports.formatTable', {}, 'Tabell')}</option>
                        <option value="chart">{t('manager.modules.claudeQuery.reports.formatChart', {}, 'Graf')}</option>
                        <option value="csv">CSV</option>
                        <option value="xlsx">Excel</option>
                    </select>
                </div>

                {/* Schedule section */}
                <div className="border border-gray-200 rounded-lg">
                    <button
                        onClick={() => setShowSchedule(!showSchedule)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        <span className="flex items-center gap-2">
                            <Clock size={16} />
                            {t('manager.modules.claudeQuery.common.scheduling', {}, 'Scheduling')}
                            {scheduleEnabled && (
                                <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">{t('manager.modules.claudeQuery.reports.active', {}, 'Aktiv')}</span>
                            )}
                        </span>
                        {showSchedule ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>

                    {showSchedule && (
                        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={scheduleEnabled}
                                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-500 focus:ring-blue-200"
                                />
                                <span className="text-sm">{t('manager.modules.claudeQuery.reports.enableAutoRun', {}, 'Aktiver automatisk kjøring')}</span>
                            </label>

                            {scheduleEnabled && (
                                <>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.common.frequency', {}, 'Frekvens')}</label>
                                            <select
                                                value={scheduleFrequency}
                                                onChange={(e) => setScheduleFrequency(e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                                            >
                                                <option value="daily">{t('manager.modules.claudeQuery.common.daily', {}, 'Daglig')}</option>
                                                <option value="weekly">{t('manager.modules.claudeQuery.common.weekly', {}, 'Ukentlig')}</option>
                                                <option value="monthly">{t('manager.modules.claudeQuery.common.monthly', {}, 'Månedlig')}</option>
                                            </select>
                                        </div>
                                        {scheduleFrequency === 'weekly' && (
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.reports.weekday', {}, 'Ukedag')}</label>
                                                <select
                                                    value={scheduleDayOfWeek}
                                                    onChange={(e) => setScheduleDayOfWeek(parseInt(e.target.value))}
                                                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                                                >
                                                    {dayNames.map((d, i) => (
                                                        <option key={i} value={i}>{d}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {scheduleFrequency === 'monthly' && (
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.reports.dayOfMonthShort', {}, 'Dag i mnd')}</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={28}
                                                    value={scheduleDayOfMonth}
                                                    onChange={(e) => setScheduleDayOfMonth(parseInt(e.target.value) || 1)}
                                                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.common.time', {}, 'Klokkeslett')}</label>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={23}
                                                    value={scheduleHour}
                                                    onChange={(e) => setScheduleHour(parseInt(e.target.value) || 0)}
                                                    className="w-14 px-2 py-1.5 border border-gray-200 rounded text-sm text-center"
                                                />
                                                <span>:</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={59}
                                                    value={scheduleMinute}
                                                    onChange={(e) => setScheduleMinute(parseInt(e.target.value) || 0)}
                                                    className="w-14 px-2 py-1.5 border border-gray-200 rounded text-sm text-center"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delivery */}
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.reports.delivery', {}, 'Levering')}</label>
                                            <select
                                                value={deliveryMethod}
                                                onChange={(e) => setDeliveryMethod(e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                                            >
                                                <option value="inapp">{t('manager.modules.claudeQuery.reports.deliveryInApp', {}, 'I appen')}</option>
                                                <option value="email">{t('manager.modules.claudeQuery.reports.deliveryEmail', {}, 'E-post')}</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.reports.fileFormat', {}, 'Filformat')}</label>
                                            <select
                                                value={deliveryFormat}
                                                onChange={(e) => setDeliveryFormat(e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                                            >
                                                <option value="xlsx">Excel</option>
                                                <option value="csv">CSV</option>
                                                <option value="html">HTML</option>
                                                <option value="json">JSON</option>
                                            </select>
                                        </div>
                                        {deliveryMethod === 'email' && (
                                            <div className="col-span-2 md:col-span-1">
                                                <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                                                    <Mail size={12} />
                                                    {t('manager.modules.claudeQuery.reports.emailAddress', {}, 'E-postadresse')}
                                                </label>
                                                <input
                                                    type="email"
                                                    value={deliveryEmail}
                                                    onChange={(e) => setDeliveryEmail(e.target.value)}
                                                    placeholder={t('manager.modules.claudeQuery.reports.emailPlaceholder', {}, 'bruker@firma.no')}
                                                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <p className="text-xs text-gray-400 mt-1">
                                        {t('manager.modules.claudeQuery.reports.cronLabel', {}, 'Cron:')} <code className="bg-gray-100 px-1 rounded">{buildCronExpression()}</code>
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Advanced section */}
                <div className="border border-gray-200 rounded-lg">
                    <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        <span>{t('manager.modules.claudeQuery.reports.advancedSection', {}, 'Avansert (Claude-formatering)')}</span>
                        {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>

                    {showAdvanced && (
                        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={useClaude}
                                    onChange={(e) => setUseClaude(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-500 focus:ring-blue-200"
                                />
                                <span className="text-sm">{t('manager.modules.claudeQuery.reports.useClaudeFormat', {}, 'Bruk Claude til å formatere resultatet')}</span>
                            </label>

                            {useClaude && (
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">
                                        {t('manager.modules.claudeQuery.reports.formatInstructions', {}, 'Formateringsinstruksjoner')}
                                    </label>
                                    <textarea
                                        value={formatPrompt}
                                        onChange={(e) => setFormatPrompt(e.target.value)}
                                        placeholder={t('manager.modules.claudeQuery.reports.formatPromptPlaceholder', {}, "f.eks. 'Lag en HTML-tabell med røde rader for varer under minstebeholdning...'")}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none
                                                 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
